import { isPlatform } from '@ionic/react';
import { useMediaQuery } from './useMediaQuery';

export interface PlatformInfo {
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  isIos: boolean;
  isAndroid: boolean;
  isWeb: boolean;
  isNative: boolean;
  osType: 'ios' | 'android' | 'web';
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

/**
 * Hook to detect platform and device type
 * Returns information about the current platform (iOS, Android, Web)
 * and device type (mobile, tablet, desktop)
 * 
 * Usage:
 * const platform = usePlatform();
 * if (platform.isMobile && platform.isAndroid) {
 *   // Android mobile - use call-through mode
 * }
 */
export function usePlatform(): PlatformInfo {
  // Media queries for device detection
  const isMobileScreen = useMediaQuery('(max-width: 768px)');
  const isTabletScreen = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
  const isDesktopScreen = useMediaQuery('(min-width: 1025px)');

  // Platform info is a pure synchronous derivation of the media queries
  // above (plus Capacitor's isPlatform(), which doesn't change at runtime),
  // computed directly during render rather than recomputed via setState in
  // an effect.
  const isIos = isPlatform('ios');
  const isAndroid = isPlatform('android');
  const isWeb = isPlatform('mobileweb') || isPlatform('desktop');
  const isNative = isIos || isAndroid;

  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (isMobileScreen) {
    deviceType = 'mobile';
  } else if (isTabletScreen) {
    deviceType = 'tablet';
  }

  let osType: 'ios' | 'android' | 'web' = 'web';
  if (isIos) {
    osType = 'ios';
  } else if (isAndroid) {
    osType = 'android';
  }

  return {
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isIos,
    isAndroid,
    isWeb,
    isNative,
    osType,
    deviceType,
  };
}

/**
 * Hook specifically for determining call mode (VoIP vs call-through)
 * 
 * Call Mode Decision:
 * - Desktop/Web: VoIP mode (Twilio Voice SDK)
 * - Mobile/Native: Call-through mode (tel: URL)
 */
export function useCallMode() {
  const platform = usePlatform();

  return {
    isVoipMode: platform.isDesktop || (platform.isWeb && !platform.isNative),
    isCallThroughMode: platform.isMobile || platform.isNative,
    platform,
  };
}
